import type { FormEventHandler, RefObject } from 'react'
import { useIntl } from 'react-intl'
import ReCAPTCHA from 'react-google-recaptcha'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import config from 'constants/config'
import { PENDING_ACTIONS, type PendingAction } from '../constants'
import { ErrorBanner, OrLabel, OrRow, PendingButtonContent, StyledAccountAccessUserInput, StyledSocialButtons, Title } from './shared'

interface Props {
  autoFocus: boolean
  error: any
  processing: boolean
  recaptchaRef: RefObject<ReCAPTCHA | null>
  onCheckUser: FormEventHandler<HTMLFormElement>
  onPrivateAppleEmailLogin: (data: App.AppleLoginData, email: string) => void
  onSocialInteractionCapture: () => void
  isActionPending: (action: PendingAction) => boolean
}

function StartStep({
  autoFocus,
  error,
  processing,
  recaptchaRef,
  onCheckUser,
  onPrivateAppleEmailLogin,
  onSocialInteractionCapture,
  isActionPending,
}: Props) {
  const intl = useIntl()

  return (
    <>
      <Title>{intl.formatMessage({
        id: 'yprcTyFTLZoK0DTf7iKt',
        defaultMessage: 'Log in or sign up',
        description: 'Title for the embedded trip planner chat account access widget',
      })}
      </Title>
      <VerticalSpacer as="form" gap={16} onSubmit={onCheckUser}>
        <ErrorBanner
          error={error}
          genericMessage={intl.formatMessage({
            id: 'fru8EaNI9dfGrLr7HbmQ',
            defaultMessage: 'Something went wrong. Please try again.',
            description: 'Generic server error message for embedded trip planner account access',
          })}
        />
        <StyledAccountAccessUserInput autoFocus={autoFocus} />
        <TextButton kind="primary" shape="round" size="medium" fit="flex" type="submit" disabled={processing}>
          <PendingButtonContent
            pending={isActionPending(PENDING_ACTIONS.checkUser)}
            label={intl.formatMessage({
              id: 'l-OntyzIV9NVn1GxzNSI',
              defaultMessage: 'Continue',
              description: 'Button text to continue to the next step in the multi-step booking flow',
            })}
          />
        </TextButton>
        {Boolean(config.RECAPTCHA_KEY) && (
          <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={recaptchaRef} />
        )}
      </VerticalSpacer>
      {(config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID) && (
        <>
          <OrRow>
            <Divider kind="primary" />
            <OrLabel variant="medium" colour="light-foreground-alt">
              {intl.formatMessage({
                id: 'pvuuq7EvzXo38JF3v2OL',
                defaultMessage: 'Other ways to log in',
                description: 'Text for social login section divider on the login or signup page',
              })}
            </OrLabel>
            <Divider kind="primary" />
          </OrRow>
          <div onClickCapture={onSocialInteractionCapture}>
            <StyledSocialButtons
              mode="login"
              inline
              lastUsedLoginMethod={undefined}
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
            />
          </div>
        </>
      )}
    </>
  )
}

export default StartStep
